import { CommonModule } from '@angular/common';
import {
    Component,
    effect,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Signal,
    SimpleChanges,
    untracked
} from '@angular/core';
import { NumberInputComponent } from '../number-input/number-input.component';
import { select } from '@ngxs/store';
import { CodState } from '../../../cod/state/cod.state';
import { ButtonModule, CheckboxModule, InputModule, TooltipModule } from '@sag-ui';
import { IOrderLine } from '@cod-monorepo/interfaces';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import {
    getOrderLineSelectionId,
    orderLineBusinessKey,
    SelectableOrderLine
} from '../../../cod/models/selectable-order-line.model';

@Component({
    selector: 'app-order-line-table',
    templateUrl: './order-line-table.component.html',
    imports: [
        CommonModule,
        CheckboxModule,
        ButtonModule,
        NumberInputComponent,
        TooltipModule,
        InputModule,
        ReactiveFormsModule
    ],
    standalone: true
})
export class OrderLineTableComponent implements OnChanges, OnInit {
    @Input() selected: IOrderLine[] | null;
    @Input() disabled = false;
    @Input() currency: 'EUR' | 'USD' = 'USD';

    @Output() selectionChange: EventEmitter<IOrderLine[]> = new EventEmitter();

    public items: Signal<SelectableOrderLine[]> = select(CodState.orderLineItems);
    public filteredItems: SelectableOrderLine[] = this.items();
    public quantities: { [key: string]: number } = {};
    public totalQuantity = 0;
    public searchControl = new FormControl<string>('');

    constructor() {
        effect(() => {
            this.items();
            untracked(() => {
                this.restoreSelection();
                this.filterItems((this.searchControl.value ?? '').toLowerCase());
            });
        });
    }

    public ngOnInit(): void {
        this.initSearchListener();
        this.filterItems('');
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['selected']) {
            this.restoreSelection();
        }
    }

    public getQuantity(item: SelectableOrderLine): number {
        return this.quantities[this.getItemId(item)] || 0;
    }

    public setQuantity(item: SelectableOrderLine, quantity: number): void {
        this.quantities[this.getItemId(item)] = quantity;
        this.calcTotalQuantity();
        this.selectionChange.emit(this.mapQuantitiesToItems());
    }

    public isChecked(item: SelectableOrderLine): boolean {
        return this.quantities[this.getItemId(item)] !== undefined;
    }

    public toggleItem(item: SelectableOrderLine): void {
        if (this.isChecked(item)) {
            delete this.quantities[this.getItemId(item)];
            this.selectionChange.emit(this.mapQuantitiesToItems());
        } else {
            this.setQuantity(item, item.salesOrderQuantity);
        }

        this.calcTotalQuantity();
    }

    public selectAll(): void {
        if (this.allItemsSelected()) {
            this.quantities = {};
        } else {
            this.items().forEach(item => {
                if (!this.isChecked(item)) {
                    this.quantities[this.getItemId(item)] = item.salesOrderQuantity;
                }
            });
        }
        this.calcTotalQuantity();
        this.selectionChange.emit(this.mapQuantitiesToItems());
    }

    public calcTotalQuantity(): void {
        this.totalQuantity = this.mapQuantitiesToItems().reduce((sum, item) => sum + item.affectedQuantity, 0);
    }

    public allItemsSelected(): boolean {
        return this.items().every(item => this.isChecked(item));
    }

    public someItemsSelected(): boolean {
        return this.items().some(item => this.isChecked(item));
    }

    public removeLeadingZeros(value: string): string {
        return value.replace(/^0+/, '');
    }

    private mapQuantitiesToItems(): IOrderLine[] {
        return this.items()
            .map(item => {
                if (this.quantities[this.getItemId(item)] === undefined) {
                    return null;
                }

                return {
                    ...item,
                    affectedQuantity: this.quantities[this.getItemId(item)]
                };
            })
            .filter(item => item !== null);
    }

    public getItemId(item: SelectableOrderLine): string {
        return item.selectionId;
    }

    private restoreSelection(): void {
        const remaining = new Set(this.items());
        this.quantities = {};
        const selections = (this.selected ?? []).map(selected => {
            const selectionId = getOrderLineSelectionId(selected);
            const row = [...remaining].find(item => item.selectionId === selectionId);
            if (row) {
                remaining.delete(row);
            }
            return { selected, row };
        });
        for (const selection of selections) {
            const { selected } = selection;
            const row =
                selection.row ??
                [...remaining].find(item => orderLineBusinessKey(item) === orderLineBusinessKey(selected));
            if (!row) {
                if (this.items().length > 0) {
                    console.error('Unable to restore an affected order line from the available order lines.', selected);
                }
                continue;
            }
            remaining.delete(row);
            this.quantities[row.selectionId] = selected.affectedQuantity;
        }
        this.calcTotalQuantity();
    }

    private initSearchListener(): void {
        this.searchControl.valueChanges
            .pipe(
                tap(value => {
                    const searchValue = (value ?? '').toLowerCase();
                    this.filterItems(searchValue);
                })
            )
            .subscribe();
    }

    private filterItems(search: string): void {
        this.filteredItems = this.items()
            .filter(
                item =>
                    item.smn.toLowerCase().includes(search) ||
                    item.materialDescription.toLowerCase().includes(search) ||
                    String(item.handlingUnit).toLowerCase().includes(search) ||
                    String(item.deliveryNumber ?? '')
                        .toLowerCase()
                        .includes(search) ||
                    String(item.batch ?? '')
                        .toLowerCase()
                        .includes(search) ||
                    item.sourceCountry.toLowerCase().includes(search) ||
                    item.shipToCountryCode.toLowerCase().includes(search) ||
                    item.salesOrderLine.toString().includes(search)
            )
            .sort((a, b) => (a?.salesOrderLine ?? 0) - (b?.salesOrderLine ?? 0));
    }
}
